package dev.kuml.portal.web.editor

/**
 * Derives a speaking project name after a project's first successful diagram generation
 * (mustFix 3 -- see the design spec's "AUTOMATISCHE PROJEKT-BENENNUNG NACH ERSTGENERIERUNG"). Pure,
 * DOM-free logic so it is directly unit-testable (see `ProjectNameDeriverTest`).
 *
 * Two candidate sources, first one that yields something usable wins:
 * 1. [diagramTitle] -- a title/root-node name extracted from the just-generated DSL (see
 *    [extractDiagramTitle]), if the DSL happens to expose one.
 * 2. The first sensible clause of [prompt] -- the leading filler word ("Ein"/"Eine"/"Der"/"Die"/
 *    "Das") is stripped, the text is cut at the first clause boundary (comma/semicolon/colon/
 *    sentence end) or, failing that, at ~40 characters on a word boundary, then capitalized and
 *    stripped of trailing punctuation.
 *
 * Returns `null` when neither source yields anything usable (e.g. an empty/only-punctuation
 * prompt and no title) -- callers must leave the project's existing default name in place in that
 * case (the design spec's "Mindest-Garantie": the project list makes such default-named projects
 * visually distinguishable via their full creation time instead).
 */
public object ProjectNameDeriver {
    // R-18: English articles were added alongside the German ones -- deliberately NOT coupled to
    // the active UI language (see this wave's plan §5.12): the prompt's language is independent
    // of the interface language (a German-UI user may prompt in English and vice versa), so a
    // language-gated filter list would strip the wrong articles in exactly that common case.
    private val fillerWords = setOf("ein", "eine", "der", "die", "das", "a", "an", "the")
    private val clauseDelimiters = charArrayOf(',', ';', ':', '.', '!', '?')
    private const val MAX_LENGTH = 40

    /** Best-effort extraction of a quoted title from the first top-level DSL call, e.g.
     * `classDiagram("Login Flow") { ... }` -> `"Login Flow"`. Returns `null` if no such literal is
     * found -- callers then fall back to [derive]'s prompt-based path. */
    public fun extractDiagramTitle(dslSource: String): String? {
        val match = titleRegex.find(dslSource) ?: return null
        return cleanupCandidate(match.groupValues[1])
    }

    /** See this object's KDoc for the two-source precedence rule. */
    public fun derive(
        diagramTitle: String?,
        prompt: String,
    ): String? {
        val fromTitle = diagramTitle?.let { cleanupCandidate(it) }
        if (!fromTitle.isNullOrBlank()) return fromTitle
        return deriveFromPrompt(prompt)
    }

    internal fun deriveFromPrompt(prompt: String): String? {
        var text = prompt.trim()
        if (text.isEmpty()) return null

        val firstSpace = text.indexOf(' ')
        if (firstSpace > 0) {
            val firstWord = text.substring(0, firstSpace)
            if (firstWord.lowercase() in fillerWords) {
                text = text.substring(firstSpace + 1).trimStart()
            }
        }

        val delimiterIndex = text.indexOfFirst { it in clauseDelimiters }
        if (delimiterIndex >= 0) {
            text = text.substring(0, delimiterIndex)
        }
        text = text.trim()
        if (text.isEmpty()) return null

        if (text.length > MAX_LENGTH) {
            val truncated = text.substring(0, MAX_LENGTH)
            val lastSpace = truncated.lastIndexOf(' ')
            text = (if (lastSpace > 0) truncated.substring(0, lastSpace) else truncated).trim()
        }

        return cleanupCandidate(text)
    }

    private fun cleanupCandidate(raw: String): String? {
        val trimmed = raw.trim().trimEnd(*clauseDelimiters).trim()
        if (trimmed.isEmpty()) return null
        return trimmed.replaceFirstChar { c -> if (c.isLowerCase()) c.titlecase() else c.toString() }
    }

    private val titleRegex = Regex("""[A-Za-z_][A-Za-z0-9_]*\s*\(\s*"([^"]{1,60})"""")
}
